import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PlayCircle, BookOpen, LogOut, CheckCircle2 } from "lucide-react";
import { studentApi, getStudentToken, setStudentToken } from "../lib/studentAuth";

const CourseDashboard = () => {
    const [me, setMe] = useState(null);
    const [error, setError] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        if (!getStudentToken()) {
            navigate("/course/login");
            return;
        }
        studentApi.get("/students/me")
            .then((r) => setMe(r.data))
            .catch(() => {
                setStudentToken(null);
                setError("Session expired. Please log in again.");
                setTimeout(() => navigate("/course/login"), 1500);
            });
    }, [navigate]);

    const logout = () => {
        setStudentToken(null);
        navigate("/course/login");
    };

    if (error) return <div className="min-h-screen bg-black text-white/40 flex items-center justify-center font-stencil text-xs tracking-widest">{error}</div>;
    if (!me) return <div className="min-h-screen bg-black text-white/40 flex items-center justify-center font-stencil text-xs tracking-widest">LOADING YOUR COURSE...</div>;

    const course = me.courses?.[0];
    if (!course) return <div className="min-h-screen bg-black text-white/60 flex items-center justify-center px-6"><div className="text-center"><h1 className="font-display text-3xl uppercase">No course found.</h1><Link to="/course" className="mt-4 btn-ghost inline-flex">Browse the course</Link></div></div>;

    const totalLessons = course.modules.reduce((sum, m) => sum + m.lessons.length, 0);

    return (
        <div className="bg-black text-white min-h-screen" data-testid="course-dashboard-page">
            <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12 md:py-20">
                {/* Header */}
                <div className="flex items-start justify-between flex-wrap gap-6 mb-12">
                    <div>
                        <div className="font-stencil tracking-widest text-xs text-red-600 mb-3">[ WELCOME{me.first_name ? `, ${me.first_name.toUpperCase()}` : ""} ]</div>
                        <h1 className="font-display text-4xl md:text-6xl uppercase tracking-tighter leading-[0.9]">
                            {course.title.split(" ").slice(0, -1).join(" ")} <span className="text-red-600">{course.title.split(" ").slice(-1)}.</span>
                        </h1>
                        <p className="mt-4 text-white/55">{course.subtitle}</p>
                    </div>
                    <button onClick={logout} className="btn-ghost text-xs" data-testid="course-logout">
                        <LogOut size={14} /> Sign out
                    </button>
                </div>

                {/* Progress stat */}
                <div className="grid grid-cols-3 gap-px bg-white/10 border border-white/10 mb-12">
                    <div className="bg-black p-5">
                        <div className="font-stencil text-[10px] tracking-widest text-white/40">MODULES</div>
                        <div className="font-display text-3xl mt-1">{course.modules.length}</div>
                    </div>
                    <div className="bg-black p-5">
                        <div className="font-stencil text-[10px] tracking-widest text-white/40">LESSONS</div>
                        <div className="font-display text-3xl mt-1">{totalLessons}</div>
                    </div>
                    <div className="bg-black p-5">
                        <div className="font-stencil text-[10px] tracking-widest text-red-600">ACCESS</div>
                        <div className="font-display text-3xl mt-1 text-red-600">∞</div>
                    </div>
                </div>

                {/* Modules grid */}
                <div className="space-y-12">
                    {course.modules.map((m) => (
                        <div key={m.id} data-testid={`dashboard-module-${m.id}`}>
                            <div className="flex items-baseline gap-6 mb-6">
                                <div className="font-display text-5xl md:text-6xl text-red-600 leading-none">0{m.number}</div>
                                <div>
                                    <h2 className="font-display text-2xl md:text-3xl uppercase tracking-tight">{m.title}</h2>
                                    <p className="text-sm text-white/55 italic">{m.subtitle}</p>
                                </div>
                            </div>
                            <div className="space-y-2">
                                {m.lessons.map((l) => (
                                    <Link
                                        key={l.id}
                                        to={`/course/lesson/${m.id}/${l.id}`}
                                        className="border border-white/10 p-5 flex items-center gap-4 hover:border-red-700/60 hover:bg-[#0a0a0a] transition-colors group"
                                        data-testid={`lesson-link-${l.id}`}
                                    >
                                        <div className="bg-red-700/15 border border-red-700/40 p-2 flex-shrink-0 group-hover:bg-red-700 transition-colors">
                                            <PlayCircle size={18} className="text-red-600 group-hover:text-white transition-colors" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <span className="font-stencil text-[10px] tracking-widest text-red-600">LESSON {l.number}</span>
                                                <span className="font-stencil text-[10px] tracking-widest text-white/30">· {l.duration_min} MIN</span>
                                                {!l.video_url && (
                                                    <span className="font-stencil text-[10px] tracking-widest text-yellow-500/70">· COMING SOON</span>
                                                )}
                                            </div>
                                            <div className="font-display text-base md:text-lg uppercase tracking-tight mt-1">{l.title}</div>
                                            <div className="text-sm text-white/55 mt-1 line-clamp-1">{l.description}</div>
                                        </div>
                                        {l.workbook_url && <BookOpen size={16} className="text-white/40 flex-shrink-0" />}
                                    </Link>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default CourseDashboard;
